(() => {
  'use strict';

  if (window.TRMFitmentGuardV1?.booted) return;

  const STORAGE_KEY = 'trmSelectedVehicleV1';
  const SELECT_VEHICLE_URL = '/pages/shop-by-vehicle?view=shop-by-vehicle';
  const CART_UPDATE_URL = window.routes?.cart_update_url || '/cart/update.js';

  const STATUS = {
    checking: {
      code: 'checking',
      label: 'Checking fitment',
      title: 'Verifying product fitment',
      message: 'We are checking this product against your selected vehicle.',
      addAllowed: false,
      fastCheckoutAllowed: false
    },
    exact_selection_required: {
      code: 'exact_selection_required',
      label: 'Vehicle Details Required',
      title: 'Confirm this part fits',
      message: 'Select the remaining vehicle details before ordering.',
      addAllowed: false,
      fastCheckoutAllowed: false
    },
    exact_confirmed_match: {
      code: 'exact_confirmed_match',
      label: 'Fit Confirmed',
      title: 'Fits your vehicle',
      message: 'Fitment is confirmed for the selected vehicle.',
      addAllowed: true,
      fastCheckoutAllowed: true
    },
    exact_mismatch: {
      code: 'exact_mismatch',
      label: "Doesn't Fit",
      title: "This part doesn't fit this vehicle",
      message: 'Choose another vehicle or request fitment help.',
      addAllowed: false,
      fastCheckoutAllowed: false
    },
    confirmed_match: {
      code: 'confirmed_match',
      label: 'Confirmed Match',
      title: 'Fits your selected vehicle',
      message: 'This product is assigned to your selected TRM Vehicle Group.',
      addAllowed: true,
      fastCheckoutAllowed: true
    },
    universal_approved: {
      code: 'universal_approved',
      label: 'Universal Approved',
      title: 'Approved universal product',
      message: 'This product is approved for sale without a vehicle-specific assignment.',
      addAllowed: true,
      fastCheckoutAllowed: true
    },
    no_vehicle_selected: {
      code: 'no_vehicle_selected',
      label: 'Fitment Not Confirmed',
      title: 'No vehicle selected',
      message: 'You can add this item, but fitment is not confirmed until a vehicle is selected.',
      addAllowed: true,
      fastCheckoutAllowed: false
    },
    fitment_mismatch: {
      code: 'fitment_mismatch',
      label: 'Different Vehicle',
      title: 'Does not match your current garage vehicle',
      message: 'You can still add this item if it is intended for another vehicle.',
      addAllowed: true,
      fastCheckoutAllowed: false
    },
    fitment_review_required: {
      code: 'fitment_review_required',
      label: 'Fitment Review Recommended',
      title: 'Fitment has not been fully verified',
      message: 'Add this item only if you have confirmed the part number or application.',
      addAllowed: true,
      fastCheckoutAllowed: false
    },
    product_not_approved: {
      code: 'product_not_approved',
      label: 'Not Available Online',
      title: 'This product is not approved for online sale',
      message: 'Please request fitment help before ordering this product.',
      addAllowed: false,
      fastCheckoutAllowed: false
    }
  };

  const normalize = (value = '') =>
    value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/\+/g, '-plus-')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const parseBoolean = (value) =>
    value === true || value === 'true' || value === '1';

  const parseVehicleGroups = (value = '') =>
    value
      .split(',')
      .map((item) => normalize(item))
      .filter(Boolean);

  const readVehicle = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const vehicle = JSON.parse(raw);
      if (!vehicle?.key || !vehicle?.label) return null;

      return {
        ...vehicle,
        key: normalize(vehicle.key),
        label: vehicle.label.toString().trim()
      };
    } catch (error) {
      return null;
    }
  };

  const readFitmentData = (element) => ({
    productId: element.dataset.trmProductId || '',
    productHandle: element.dataset.trmProductHandle || '',
    productTitle: element.dataset.trmProductTitle || '',
    vehicleGroups: parseVehicleGroups(element.dataset.trmVehicleGroups || ''),
    universalApproved: parseBoolean(
      element.dataset.trmUniversalApproved
    ),
    intakeStatus: normalize(element.dataset.trmIntakeStatus || ''),
    exactStatus: element.dataset.trmExactStatus || '',
    exactLabel: element.dataset.trmExactLabel || '',
    exactKey: element.dataset.trmExactKey || ''
  });

  const evaluate = (fitmentData, vehicle) => {
    if (fitmentData.intakeStatus !== 'published') {
      return STATUS.product_not_approved;
    }

    if (fitmentData.exactStatus === 'selection_required') {
      return STATUS.exact_selection_required;
    }

    if (fitmentData.exactStatus === 'confirmed_match') {
      return STATUS.exact_confirmed_match;
    }

    if (fitmentData.exactStatus === 'mismatch') {
      return STATUS.exact_mismatch;
    }

    if (fitmentData.universalApproved) {
      return STATUS.universal_approved;
    }

    if (fitmentData.vehicleGroups.length > 0) {
      if (!vehicle?.key) {
        return STATUS.no_vehicle_selected;
      }

      if (fitmentData.vehicleGroups.includes(normalize(vehicle.key))) {
        return STATUS.confirmed_match;
      }

      return STATUS.fitment_mismatch;
    }

    return STATUS.fitment_review_required;
  };

  const setText = (root, selector, value) => {
    const element = root.querySelector(selector);

    if (!element) return;

    const nextValue = value?.toString() || '';

    if (element.textContent !== nextValue) {
      element.textContent = nextValue;
    }
  };

  const setProperty = (form, key, value, enabled) => {
    const input = form?.querySelector(
      `[data-trm-fitment-property="${key}"]`
    );

    if (!input) return;

    const nextValue = value || '';
    const nextDisabled = !enabled;

    if (input.value !== nextValue) {
      input.value = nextValue;
    }

    if (input.disabled !== nextDisabled) {
      input.disabled = nextDisabled;
    }
  };

  const updateProductGuard = (guard, vehicle) => {
    const fitmentData = readFitmentData(guard);
    const status = evaluate(fitmentData, vehicle);
    const formId = guard.dataset.trmProductFormId || '';
    const form = document.getElementById(formId);

    guard.dataset.trmFitmentStatus = status.code;

    setText(guard, '[data-trm-fitment-badge]', status.label);
    setText(guard, '[data-trm-fitment-title]', status.title);
    setText(guard, '[data-trm-fitment-message]', status.message);
    const displayedVehicleLabel =
      fitmentData.exactLabel || vehicle?.label || 'None selected';

    setText(
      guard,
      '[data-trm-fitment-vehicle]',
      displayedVehicleLabel
    );

    const changeLink = guard.querySelector(
      '[data-trm-fitment-change-vehicle]'
    );

    if (changeLink) {
      changeLink.textContent = vehicle?.key || fitmentData.exactLabel
        ? 'CHANGE VEHICLE'
        : 'SELECT VEHICLE';
    }

    if (!form) return;

    form.dataset.trmFitmentStatus = status.code;
    form.dataset.trmFitmentAllowed =
      status.addAllowed ? 'true' : 'false';
    form.dataset.trmFitmentFastCheckoutAllowed =
      status.fastCheckoutAllowed ? 'true' : 'false';

    const variantInput = form.querySelector('input[name="id"]');
    const addButton = form.querySelector('[data-trm-fitment-submit]');
    const variantAvailable = Boolean(
      variantInput?.value && !variantInput.disabled
    );

    const canAdd =
      status.addAllowed && variantAvailable;

    const canFastCheckout =
      status.fastCheckoutAllowed && variantAvailable;

    if (addButton) {
      addButton.disabled = !canAdd;
      addButton.setAttribute(
        'aria-disabled',
        canAdd ? 'false' : 'true'
      );
      addButton.dataset.trmFitmentBlocked =
        canAdd ? 'false' : 'true';
    }

    guard
      .closest('.product-block')
      ?.querySelectorAll('[data-trm-dynamic-checkout]')
      .forEach((element) => {
        element.hidden = !canFastCheckout;
        element.dataset.trmFitmentAllowed =
          canFastCheckout ? 'true' : 'false';
      });

    const vehicleLabel = fitmentData.exactLabel ||
      (status.code === 'universal_approved' && !vehicle?.label
        ? 'Universal / Vehicle-independent'
        : vehicle?.label || 'Not selected');

    const vehicleKey = fitmentData.exactKey || vehicle?.key || '';

    setProperty(
      form,
      'vehicle_label',
      vehicleLabel,
      status.addAllowed
    );
    setProperty(
      form,
      'status_label',
      status.label,
      status.addAllowed
    );
    setProperty(
      form,
      'vehicle_key',
      vehicleKey,
      status.addAllowed
    );
    setProperty(
      form,
      'status_code',
      status.code,
      status.addAllowed
    );
    setProperty(
      form,
      'product_handle',
      fitmentData.productHandle,
      status.addAllowed
    );
  };

  const updateVisibleLineStatus = (line, status, vehicle) => {
    const statusBox = line.querySelector('[data-trm-line-status]');
    if (!statusBox) return;

    const actionable = [
      'no_vehicle_selected',
      'fitment_mismatch',
      'fitment_review_required'
    ].includes(status.code);

    statusBox.dataset.trmFitmentStatus = status.code;
    statusBox.dataset.trmFitmentActionable =
      actionable ? 'true' : 'false';

    if (actionable) {
      statusBox.setAttribute('role', 'button');
      statusBox.setAttribute('tabindex', '0');
      statusBox.setAttribute(
        'aria-label',
        `${status.label}. Change selected vehicle.`
      );
    } else {
      statusBox.removeAttribute('role');
      statusBox.removeAttribute('tabindex');
      statusBox.removeAttribute('aria-label');
    }

    setText(statusBox, '[data-trm-line-status-label]', status.label);

    let message = status.message;

    if (
      status.code === 'exact_confirmed_match' &&
      line.dataset.trmExactLabel
    ) {
      message = `Fits ${line.dataset.trmExactLabel}.`;
    } else if (status.code === 'confirmed_match' && vehicle?.label) {
      message = `Confirmed for ${vehicle.label}.`;
    } else if (
      status.code === 'fitment_mismatch' &&
      vehicle?.label
    ) {
      message = `This item is not assigned to ${vehicle.label}.`;
    }

    setText(
      statusBox,
      '[data-trm-line-status-message]',
      message
    );
  };

  const summarizeCart = (results, vehicle) => {
    if (results.length === 0) {
      return {
        status: 'checking',
        title: 'Cart is empty',
        message: 'Add a product to begin fitment validation.',
        allowed: false,
        expressAllowed: false
      };
    }

    const hardBlocked = results.filter(
      (result) =>
        [
          'product_not_approved',
          'exact_selection_required',
          'exact_mismatch'
        ].includes(result.status.code)
    ).length;

    const mismatches = results.filter(
      (result) =>
        result.status.code === 'fitment_mismatch'
    ).length;

    const reviewRequired = results.filter(
      (result) =>
        result.status.code === 'fitment_review_required'
    ).length;

    const vehicleRequired = results.filter(
      (result) =>
        result.status.code === 'no_vehicle_selected'
    ).length;

    const warningCount =
      mismatches + reviewRequired + vehicleRequired;

    if (hardBlocked > 0) {
      return {
        status: 'blocked',
        title: 'Checkout unavailable',
        message: `${hardBlocked} cart item${hardBlocked === 1 ? '' : 's'} require a compatible vehicle selection or product approval before checkout.`,
        allowed: false,
        expressAllowed: false
      };
    }

    if (warningCount > 0) {
      return {
        status: 'warning',
        title: 'Review vehicle fitment before checkout',
        message: `${warningCount} cart item${warningCount === 1 ? '' : 's'} are for a different vehicle or have unconfirmed fitment. Checkout remains available.`,
        allowed: true,
        expressAllowed: false
      };
    }

    const exactLabels = Array.from(
      new Set(
        results
          .filter(
            (result) =>
              result.status.code ===
                'exact_confirmed_match' &&
              result.fitmentData.exactLabel
          )
          .map(
            (result) =>
              result.fitmentData.exactLabel
          )
      )
    );

    let approvedMessage =
      'All cart items passed their recorded fitment checks.';

    if (exactLabels.length === 1) {
      approvedMessage = `All cart items fit ${exactLabels[0]}.`;
    } else if (vehicle?.label) {
      approvedMessage = `All cart items are approved for ${vehicle.label} or are approved universal products.`;
    }

    return {
      status: 'approved',
      title: 'Vehicle fitment confirmed',
      message: approvedMessage,
      allowed: true,
      expressAllowed: true
    };
  };

  const updateCartContext = (context, vehicle) => {
    const lines = Array.from(
      document.querySelectorAll(
        `[data-trm-cart-line][data-trm-fitment-context="${context}"]`
      )
    );

    const results = lines.map((line) => {
      const fitmentData = readFitmentData(line);
      const status = evaluate(fitmentData, vehicle);

      line.dataset.trmFitmentStatus = status.code;
      updateVisibleLineStatus(line, status, vehicle);

      return { line, fitmentData, status };
    });

    const summary = summarizeCart(results, vehicle);

    document
      .querySelectorAll(
        `[data-trm-cart-summary][data-trm-fitment-context="${context}"]`
      )
      .forEach((element) => {
        element.dataset.trmFitmentStatus = summary.status;
        element.dataset.trmFitmentAllowed = summary.allowed
          ? 'true'
          : 'false';

        const actionable =
          summary.status === 'warning';

        element.dataset.trmFitmentActionable =
          actionable ? 'true' : 'false';

        if (actionable) {
          element.setAttribute('role', 'button');
          element.setAttribute('tabindex', '0');
          element.setAttribute(
            'aria-label',
            'Review vehicle fitment. Change selected vehicle.'
          );
        } else {
          element.removeAttribute('role');
          element.removeAttribute('tabindex');
          element.removeAttribute('aria-label');
        }

        setText(
          element,
          '[data-trm-cart-summary-title]',
          summary.title
        );
        setText(
          element,
          '[data-trm-cart-summary-message]',
          summary.message
        );

        const changeLink = element.querySelector(
          '[data-trm-fitment-change-vehicle]'
        );

        if (changeLink) {
          changeLink.textContent = vehicle?.key
            ? 'CHANGE VEHICLE'
            : 'SELECT VEHICLE';
        }
      });

    document
      .querySelectorAll(
        `[data-trm-checkout-button][data-trm-fitment-context="${context}"]`
      )
      .forEach((button) => {
        button.disabled = !summary.allowed;
        button.setAttribute(
          'aria-disabled',
          summary.allowed ? 'false' : 'true'
        );
        button.dataset.trmFitmentBlocked = summary.allowed
          ? 'false'
          : 'true';
      });

    document
      .querySelectorAll(
        `[data-trm-express-checkout][data-trm-fitment-context="${context}"]`
      )
      .forEach((element) => {
        element.hidden = !summary.expressAllowed;
        element.dataset.trmFitmentAllowed =
          summary.expressAllowed ? 'true' : 'false';
      });

    return summary;
  };

  let updateInProgress = false;

  const updateAll = () => {
    if (updateInProgress) return;

    updateInProgress = true;

    try {
      const vehicle = readVehicle();

      document
        .querySelectorAll('[data-trm-fitment-guard]')
        .forEach((guard) => updateProductGuard(guard, vehicle));

      updateCartContext('cart-page', vehicle);
      updateCartContext('mini-cart', vehicle);
    } finally {
      updateInProgress = false;
    }
  };

  let cartSyncController = null;

  const syncVehicleToCart = async (vehicle) => {
    if (cartSyncController) cartSyncController.abort();
    cartSyncController = new AbortController();

    const attributes = {
      '_TRM Vehicle Key': vehicle?.key || '',
      'TRM Vehicle': vehicle?.label || ''
    };

    try {
      await fetch(CART_UPDATE_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes }),
        signal: cartSyncController.signal
      });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn(
          'TRM Fitment Guard could not sync the selected vehicle to the cart.',
          error
        );
      }
    }
  };

  const openVehicleSelector = (trigger = null) => {
    if (window.TRMGarageV1?.open) {
      window.TRMGarageV1.open(trigger);
      return true;
    }

    window.dispatchEvent(
      new CustomEvent('trm:garage-open', {
        detail: { trigger }
      })
    );

    const garageChange = document.querySelector(
      '[data-trm-garage-change]'
    );

    const garageSelect = document.querySelector(
      '[data-trm-garage-select]'
    );

    const garageTrigger = garageChange || garageSelect;

    if (garageTrigger) {
      garageTrigger.click();
      return true;
    }

    return false;
  };

  const vehicleSelectorTrigger = (target) =>
    target.closest(
      [
        '[data-trm-fitment-change-vehicle]',
        '[data-trm-fitment-actionable="true"]'
      ].join(',')
    );

  const activateVehicleSelector = (event) => {
    const trigger = vehicleSelectorTrigger(event.target);

    if (!trigger) return false;

    event.preventDefault();
    event.stopPropagation();

    openVehicleSelector(trigger);

    return true;
  };

  const formIsAllowed = (form) =>
    form?.dataset.trmFitmentAllowed === 'true';

  document.addEventListener(
    'submit',
    (event) => {
      const productForm = event.target.closest(
        'form[data-trm-fitment-form="true"]'
      );

      if (productForm && !formIsAllowed(productForm)) {
        event.preventDefault();
        event.stopImmediatePropagation();

        productForm
          .closest('.product-block')
          ?.querySelector('[data-trm-fitment-guard]')
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

        return;
      }

      const submitter = event.submitter;
      if (submitter?.matches('[data-trm-checkout-button]')) {
        const allowed =
          submitter.dataset.trmFitmentBlocked === 'false';

        if (!allowed) {
          event.preventDefault();
          event.stopImmediatePropagation();

          document
            .querySelector(
              `[data-trm-cart-summary][data-trm-fitment-context="${submitter.dataset.trmFitmentContext}"]`
            )
            ?.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
        }
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (activateVehicleSelector(event)) {
        return;
      }

      const blockedSubmit = event.target.closest(
        '[data-trm-fitment-submit][data-trm-fitment-blocked="true"], [data-trm-checkout-button][data-trm-fitment-blocked="true"]'
      );

      if (blockedSubmit) {
        event.preventDefault();
        event.stopImmediatePropagation();
        updateAll();
        return;
      }

      const acceleratedCheckout = event.target.closest(
        '.shopify-payment-button, [data-trm-express-checkout]'
      );

      if (acceleratedCheckout) {
        const productForm = acceleratedCheckout.closest(
          'form[data-trm-fitment-form="true"]'
        );

        if (
          productForm &&
          productForm.dataset.trmFitmentFastCheckoutAllowed !== 'true'
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
          updateAll();
          return;
        }

        const contextWrapper = acceleratedCheckout.closest(
          '[data-trm-fitment-context]'
        );

        if (
          contextWrapper?.dataset.trmFitmentAllowed === 'false'
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
          updateAll();
        }
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (!['Enter', ' '].includes(event.key)) {
        return;
      }

      const trigger = vehicleSelectorTrigger(event.target);

      if (!trigger) return;

      event.preventDefault();
      openVehicleSelector(trigger);
    },
    true
  );

  const FITMENT_UI_SELECTOR = [
    '[data-trm-fitment-guard]',
    '[data-trm-line-status]',
    '[data-trm-cart-summary]'
  ].join(',');

  const mutationNeedsUpdate = (mutation) => {
    const target =
      mutation.target instanceof Element
        ? mutation.target
        : mutation.target.parentElement;

    if (target?.closest(FITMENT_UI_SELECTOR)) {
      return false;
    }

    return Array.from(
      mutation.addedNodes
    ).some((node) => node.nodeType === Node.ELEMENT_NODE) ||
      Array.from(
        mutation.removedNodes
      ).some((node) => node.nodeType === Node.ELEMENT_NODE);
  };

  let renderTimer = null;

  const observer = new MutationObserver((mutations) => {
    if (!mutations.some(mutationNeedsUpdate)) {
      return;
    }

    window.clearTimeout(renderTimer);

    renderTimer = window.setTimeout(() => {
      renderTimer = null;
      updateAll();
    }, 120);
  });

  const boot = () => {
    updateAll();
    syncVehicleToCart(readVehicle());

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  };

  window.addEventListener('trm:vehicle-changed', (event) => {
    const vehicle = event.detail || readVehicle();
    syncVehicleToCart(vehicle);
    updateAll();
  });

  window.addEventListener('trm:vehicle-cleared', () => {
    syncVehicleToCart(null);
    updateAll();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;

    syncVehicleToCart(readVehicle());
    updateAll();
  });

  document.addEventListener(
    'change',
    (event) => {
      if (
        event.target.matches(
          'input[name="id"], select[name="id"], [data-option-value-id]'
        )
      ) {
        window.setTimeout(updateAll, 50);
      }
    },
    true
  );

  window.TRMFitmentGuardV1 = {
    booted: true,
    version: '1.1.1-customer-fitment-copy',
    update: updateAll,
    readVehicle,
    evaluate,
    statuses: STATUS,
    selectVehicleUrl: SELECT_VEHICLE_URL
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, {
      once: true
    });
  } else {
    boot();
  }
})();
